import React, { FC, useLayoutEffect, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Theme, useTheme } from "@mui/material/styles";
import useStyles from "./chart.style";
import {
  ArcElement,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  Tooltip,
} from "chart.js";
import {
  MyLeaveChartLegend,
  MyLeaveChartSummary,
  MyLeaveChartSummaryLoading,
} from "my-leave/components";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

// Register necessary Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Define the shape of each data item
interface DataItem {
  label: string;
  value: number;
  color: string;
}

// Define props for the component
interface MyLeaveChartProps {
  chartSize: number;
  dataSet: DataItem[];
  summaryLabel: string;
  summaryType: string;
  summaryValue?: number;
  loading: boolean;
}

export const MyLeaveChart: FC<MyLeaveChartProps> = ({
  chartSize,
  dataSet,
  summaryLabel,
  summaryType,
  summaryValue = 0,
  loading,
}) => {
  const theme: Theme = useTheme();
  const isMobile: boolean = useOcIsMobile();

  const data: ChartData<"doughnut", number[], string> = {
    labels: dataSet.map((item) => item.label),
    datasets: [
      {
        data: dataSet.map((item) => item.value),
        backgroundColor: dataSet.map((item) => item.color),
        hoverBackgroundColor: dataSet.map((item) => item.color),
      },
    ],
  };

  const dataLoading: ChartData<"doughnut", number[], string> = {
    labels: ["1"],
    datasets: [
      {
        data: [1],
        backgroundColor: [theme.palette.gray30.main],
        hoverBackgroundColor: [theme.palette.gray30.main],
      },
    ],
  };

  const width: number = chartSize;
  const centerTextLeft: number = chartSize / 2 - 45;
  const centerTextTop: number = chartSize / 2 - 35;
  const legendWidth: number = 260;

  const componentRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useLayoutEffect(() => {
    if (componentRef.current) {
      setContainerWidth(
        componentRef.current.offsetWidth > 420
          ? componentRef.current.offsetWidth
          : 420,
      );
    }
  }, []);

  const classes = useStyles({
    // @ts-ignore
    containerWidth,
    legendWidth,
    width,
    chartSize,
    centerTextLeft,
    centerTextTop,
    isMobile,
  });

  const options: ChartOptions<"doughnut"> = {
    cutout: "72%",
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    animation: {
      duration: loading ? 0 : 1000,
    },
  };

  return (
    <div
      id="my-leave-full-chart"
      ref={componentRef}
      className={classes.chartContainerRoot}
    >
      <div className={classes.chartContainer}>
        <Doughnut
          data={loading ? dataLoading : data}
          width={width}
          height={chartSize}
          options={options}
        />
        {loading ? (
          <MyLeaveChartSummaryLoading
            centerTextLeft={centerTextLeft}
            centerTextTop={centerTextTop}
          />
        ) : (
          <MyLeaveChartSummary
            summaryValue={summaryValue}
            summaryLabel={summaryLabel}
            summaryType={summaryType}
            centerTextLeft={centerTextLeft}
            centerTextTop={centerTextTop}
          />
        )}
      </div>

      <div className={classes.legendMainContainer}>
        {dataSet.map((item, index) => (
          <MyLeaveChartLegend
            key={`${index}-${item.label}`}
            index={index}
            item={item}
            listCount={dataSet.length}
            loading={loading}
          />
        ))}
      </div>
    </div>
  );
};

export default MyLeaveChart;
